/**
 * CancellationPolicyCard -- shows 3 policy tiers with the active one highlighted.
 *
 * Uses success/warning/error semantic colors for full/partial/none.
 */
import { View, Text } from 'react-native'
import { CheckCircle, Clock, AlertCircle } from '@/components/ui/icons'
import { useTranslation } from 'react-i18next'
import { FONTS, SPACING, RADIUS } from '@/lib/brand'
import { useSemanticColors } from '@/lib/theme/tokens'
import { useTheme } from '@/lib/hooks/use-theme'
import type { CancellationPolicy } from '@/lib/types/cancellation.types'

interface CancellationPolicyCardProps {
  activePolicy: CancellationPolicy
  hoursBeforeStart: number
}

export function CancellationPolicyCard({
  activePolicy,
  hoursBeforeStart,
}: CancellationPolicyCardProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  const tiers: Array<{
    key: CancellationPolicy
    icon: React.ReactNode
    color: string
    label: string
    desc: string
  }> = [
    {
      key: 'full',
      icon: <CheckCircle size={18} color={colors.success} />,
      color: colors.success,
      label: t('cancellation.policy_full'),
      desc: t('cancellation.policy_full_desc'),
    },
    {
      key: 'partial',
      icon: <Clock size={18} color={colors.warning} />,
      color: colors.warning,
      label: t('cancellation.policy_partial'),
      desc: t('cancellation.policy_partial_desc'),
    },
    {
      key: 'none',
      icon: <AlertCircle size={18} color={colors.textError} />,
      color: colors.textError,
      label: t('cancellation.policy_none'),
      desc: t('cancellation.policy_none_desc'),
    },
  ]

  return (
    <View
      style={{
        backgroundColor: colors.surface,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: colors.border,
        padding: SPACING.md,
      }}
    >
      <Text
        style={{
          fontSize: 18,
          fontFamily: FONTS.bodyBold,
          color: colors.textPrimary,
          marginBottom: SPACING.md,
        }}
      >
        {t('cancellation.policy_title')}
      </Text>

      <Text
        style={{
          fontSize: 14,
          fontFamily: FONTS.body,
          color: colors.textSecondary,
          marginBottom: SPACING.md,
        }}
      >
        {t('cancellation.hours_before', { hours: hoursBeforeStart })}
      </Text>

      {tiers.map((tier) => {
        const isActive = tier.key === activePolicy
        return (
          <View
            key={tier.key}
            style={{
              flexDirection: 'row',
              alignItems: 'flex-start',
              gap: SPACING.sm,
              padding: SPACING.sm,
              marginBottom: SPACING.xs,
              borderRadius: RADIUS.md,
              borderWidth: isActive ? 2 : 1,
              borderColor: isActive ? tier.color : colors.border,
              backgroundColor: isActive ? `${tier.color}11` : 'transparent',
            }}
          >
            {tier.icon}
            <View style={{ flex: 1 }}>
              <Text
                style={{
                  fontSize: 14,
                  fontFamily: FONTS.bodyBold,
                  color: isActive ? tier.color : colors.textPrimary,
                }}
              >
                {tier.label}
              </Text>
              <Text
                style={{
                  fontSize: 12,
                  fontFamily: FONTS.body,
                  color: colors.textSecondary,
                  marginTop: SPACING.xs,
                }}
              >
                {tier.desc}
              </Text>
            </View>
          </View>
        )
      })}
    </View>
  )
}
